import { ReflexContainer, ReflexSplitter, ReflexElement } from "react-reflex";
import { useCallback, useState, useRef, useMemo, useEffect } from "react";
import debounce from "lodash.debounce";
import "react-reflex/styles.css";
import { Box, IconButton, Tooltip } from "@mzc-pdc/ui";
import { IconChevronLeft, IconChevronRight } from "@mzc-cloudplex/icons";
import { useTranslation } from "react-i18next";
import theme from "@styles/theme";

// Constants
const STORAGE_KEY_PREFIX = "collapsibleSplitTwoTierGrid";
const DEBOUNCE_DELAY = 300;
const ANIMATION_DURATION = 300;
const COLLAPSED_SIZE = 0.001;
const SPLITTER_WIDTH = 36;
const SHADOW_WIDTH = 12;

// CustomSplitter component
const CustomSplitter = ({ isLeftCollapsed, isAnimating, onTogglePanel, t }) => {
    const getSplitterBoxStyles = () => ({
        width: "50%",
        height: "100%",
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        ml: theme.spacing(2),
        "&::after": {
            content: '""',
            display: "block",
            position: "absolute",
            top: 0,
            bottom: 0,
            left: "50%",
            width: SHADOW_WIDTH,
            opacity: isLeftCollapsed ? 1 : 0,
            transition: "opacity 200ms cubic-bezier(0.4, 0, 0.2, 1)",
            boxShadow: "-5px 0 6px 0 rgba(0, 0, 0, 0.1)",
            transform: "translateX(-50%)",
        },
        "& .toggle-button": { opacity: 0 },
        "&:hover": {
            "&::after": { opacity: 1 },
            "& .toggle-button": { opacity: 1 },
        },
        ...(isLeftCollapsed && { "& .toggle-button": { opacity: 1 } }),
    });

    const getToggleButtonStyles = () => ({
        zIndex: 10,
        position: "absolute",
        left: "-50%",
        "&:hover": {
            backgroundColor: !isLeftCollapsed ? theme.palette.background.default : undefined,
        },
    });

    const handleMouseDown = (e) => {
        if (isLeftCollapsed || isAnimating) {
            e.stopPropagation();
            e.preventDefault();
        }
    };

    const tooltipTitle = isLeftCollapsed
        ? t("common::label::Expand panel", "Expand panel")
        : t("common::label::Collapse panel", "Collapse panel");

    return (
        <Box sx={getSplitterBoxStyles()} onMouseDown={handleMouseDown}>
            <Tooltip title={tooltipTitle}>
                <IconButton
                    circled
                    className="toggle-button"
                    onClick={onTogglePanel}
                    onMouseDown={(e) => e.stopPropagation()}
                    size="small"
                    variant={isLeftCollapsed ? "contained" : "outlined"}
                    color="greySecondary"
                    sx={getToggleButtonStyles()}
                >
                    {isLeftCollapsed ? <IconChevronRight size={12} /> : <IconChevronLeft size={12} />}
                </IconButton>
            </Tooltip>
        </Box>
    );
};

const CollapsibleSplitTwoTierGridLeft = ({
    storageKey = "Default",
    gridLeft,
    gridRight,
    orientation = "vertical",
    gridLeftProps = {
        defaultSize: 336,
        minSize: 336,
        maxSize: 740,
    },
    gridRightProps,
}) => {
    const { t } = useTranslation();
    const storageKeyName = useMemo(() => `${STORAGE_KEY_PREFIX}${storageKey}`, [storageKey]);

    const hasLeftPanel = !!gridLeft;

    const [initialState] = useState(() => {
        try {
            const item = window.localStorage.getItem(storageKeyName);
            if (item) {
                const data = JSON.parse(item);
                if (typeof data.size === "number" && typeof data.isCollapsed === "boolean") {
                    return data;
                }
            }
        } catch (error) {
            console.error("Error reading from localStorage", error);
        }
        return null;
    });

    const [isLeftCollapsed, setIsLeftCollapsed] = useState(() => initialState?.isCollapsed ?? false);
    const [leftPanelSize, setLeftPanelSize] = useState(() => {
        // Note: size 0인 경우 ReflexContainer 내부 flex 계산 이슈(flexDataInit)로 인해 size 제어가 불가
        return initialState
            ? initialState.isCollapsed
                ? COLLAPSED_SIZE
                : initialState.size
            : gridLeftProps?.defaultSize;
    });
    const [isAnimating, setIsAnimating] = useState(false);
    const animationRef = useRef(null);
    const lastValidSize = useRef(initialState?.size ?? gridLeftProps?.defaultSize);

    const saveStateToLocalStorage = useMemo(
        () =>
            debounce((size, collapsed) => {
                try {
                    const stateToSave = {
                        size: size,
                        isCollapsed: collapsed,
                    };
                    window.localStorage.setItem(storageKeyName, JSON.stringify(stateToSave));
                } catch (error) {
                    console.error("Error writing to localStorage", error);
                }
            }, DEBOUNCE_DELAY),
        [storageKeyName],
    );

    const animatePanel = useCallback((startSize, endSize, duration = ANIMATION_DURATION) => {
        if (animationRef.current) {
            window.cancelAnimationFrame(animationRef.current);
            animationRef.current = null;
        }

        setIsAnimating(true);
        const startTime = performance.now();

        const animate = (currentTime) => {
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);

            const currentSize = startSize + (endSize - startSize) * progress;
            setLeftPanelSize(currentSize);

            if (progress < 1) {
                animationRef.current = window.requestAnimationFrame(animate);
            } else {
                animationRef.current = null;
                setIsAnimating(false);
            }
        };

        animationRef.current = window.requestAnimationFrame(animate);
    }, []);

    const handleToggleLeftPanel = useCallback(() => {
        const willCollapse = !isLeftCollapsed;
        setIsLeftCollapsed(willCollapse);

        if (willCollapse) {
            lastValidSize.current = leftPanelSize;
            animatePanel(leftPanelSize, 0);
            saveStateToLocalStorage(leftPanelSize, true);
        } else {
            animatePanel(0, lastValidSize.current);
            saveStateToLocalStorage(lastValidSize.current, false);
        }
    }, [isLeftCollapsed, leftPanelSize, animatePanel, saveStateToLocalStorage]);

    const handlePanelResize = useCallback(
        (event) => {
            if (isAnimating || isLeftCollapsed) return;

            const isLeftPane = event.component.props.className === "left-pane";
            if (!isLeftPane || !event.domElement) return;

            const newSize = orientation === "horizontal" ? event.domElement.offsetHeight : event.domElement.offsetWidth;

            if (newSize > 0) {
                setLeftPanelSize(newSize);

                if (newSize >= gridLeftProps?.minSize) {
                    lastValidSize.current = newSize;
                    saveStateToLocalStorage(newSize, false);
                }
            }
        },
        [isAnimating, isLeftCollapsed, gridLeftProps?.minSize, orientation, saveStateToLocalStorage],
    );

    useEffect(() => {
        return () => {
            if (animationRef.current) {
                window.cancelAnimationFrame(animationRef.current);
            }
            saveStateToLocalStorage.cancel();
        };
    }, [saveStateToLocalStorage]);

    return (
        <ReflexContainer orientation={orientation} style={{ flex: 1, overflow: "hidden" }} windowResizeAware>
            {hasLeftPanel && ( //Note: ReflexSplitter와 fragment로 묶을 경우 ReflexContainer에서 크기 계산 불가
                <ReflexElement
                    className="left-pane"
                    size={leftPanelSize}
                    minSize={isLeftCollapsed ? 0 : gridLeftProps?.minSize}
                    maxSize={gridLeftProps?.maxSize}
                    onResize={handlePanelResize}
                >
                    <Box
                        sx={{
                            height: "100%",
                            overflow: "auto",
                            opacity: leftPanelSize < 1 ? 0 : 1,
                            ...(gridLeftProps?.style ?? {}),
                        }}
                    >
                        {gridLeft}
                    </Box>
                </ReflexElement>
            )}

            {hasLeftPanel && (
                <ReflexSplitter
                    style={{
                        width: SPLITTER_WIDTH,
                        cursor: isLeftCollapsed || isAnimating ? "default" : "col-resize",
                        backgroundColor: "transparent",
                        border: "none",
                    }}
                >
                    <CustomSplitter
                        isLeftCollapsed={isLeftCollapsed}
                        isAnimating={isAnimating}
                        onTogglePanel={handleToggleLeftPanel}
                        t={t}
                    />
                </ReflexSplitter>
            )}

            <ReflexElement className="right-pane" minSize={gridRightProps?.minSize}>
                <Box
                    sx={{
                        height: "100%",
                        overflow: "auto",
                        ...(gridRightProps?.style ?? {}),
                    }}
                >
                    {gridRight}
                </Box>
            </ReflexElement>
        </ReflexContainer>
    );
};

export default CollapsibleSplitTwoTierGridLeft;
